import React from 'react'
import { Shield } from 'lucide-react'

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdated = "December 2025"

  return (
    <div className="font-outfit text-ink-green-ink dark:text-ink-green-ink bg-ink-green-canvas dark:bg-ink-green-canvas min-h-screen">
      {/* Main content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-28 pb-6">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-1.5 bg-ink-green-accent/10 text-ink-green-accent rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 border border-ink-green-accent/20">
                <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
                Privacy & Data Protection
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-[-.03em] text-ink-green-ink dark:text-ink-green-ink mb-4 sm:mb-6 leading-tight">
                Privacy{' '}
                <span className="text-ink-green-accent">Policy</span>
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-ink-green-ink-soft dark:text-ink-green-ink-soft leading-[1.55] max-w-2xl mx-auto">
                Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
              </p>
              <p className="text-xs sm:text-sm text-ink-green-ink-soft dark:text-ink-green-ink-soft mt-3 sm:mt-4">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </section>

        {/* Privacy Policy Content */}
        <section className="relative pt-4 pb-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <div className="bg-ink-green-surface dark:bg-ink-green-surface rounded-panel border border-ink-green-edge p-8 md:p-12">
              <div className="text-sm sm:text-base text-ink-green-ink-soft dark:text-ink-green-ink-soft leading-[1.55] space-y-6 sm:space-y-8">

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">1. Information We Collect</h2>
                  <p className="mb-3 sm:mb-4">We collect information you provide directly to us, including:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Contact information (name, email address, phone number)</li>
                    <li>Investment preferences and financial information</li>
                    <li>Communication records and consultation notes</li>
                    <li>Website usage data and analytics</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">2. How We Use Your Information</h2>
                  <p className="mb-3 sm:mb-4">We use the information we collect to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Provide personalized property investment services</li>
                    <li>Send relevant market reports and investment opportunities</li>
                    <li>Communicate about our services and respond to inquiries</li>
                    <li>Improve our website and service offerings</li>
                    <li>Comply with legal and regulatory requirements</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">3. Information Sharing</h2>
                  <p className="mb-3 sm:mb-4">We do not sell, trade, or rent your personal information. We may share information only:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>With your explicit consent</li>
                    <li>With trusted service providers who assist in our operations</li>
                    <li>When required by law or regulatory authorities</li>
                    <li>In connection with a business transaction or merger</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">4. Data Security</h2>
                  <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure data storage, and regular security assessments.</p>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">5. Your Rights</h2>
                  <p className="mb-3 sm:mb-4">Under GDPR and UK data protection laws, you have the right to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Access and obtain copies of your personal data</li>
                    <li>Rectify inaccurate or incomplete information</li>
                    <li>Erase your personal data in certain circumstances</li>
                    <li>Restrict or object to processing of your data</li>
                    <li>Data portability where technically feasible</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">6. Cookies and Tracking</h2>
                  <p>We use cookies and similar technologies to improve your browsing experience and analyze website traffic. You can manage cookie preferences through your browser settings. For more details, see our Cookie Policy.</p>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">7. Contact Information</h2>
                  <p className="mb-3 sm:mb-4">For privacy-related inquiries or to exercise your rights, contact us:</p>
                  <div className="bg-ink-green-canvas dark:bg-ink-green-canvas rounded-panel p-4 sm:p-6 border border-ink-green-edge">
                    <p><strong>Email:</strong> privacy@novahass.partners</p>
                    <p><strong>Address:</strong> 7 Bell Yard, London, WC2A 2JR</p>
                    <p><strong>Phone:</strong> Available upon request</p>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">8. Updates to This Policy</h2>
                  <p>We may update this Privacy Policy periodically. We will notify you of any significant changes via email or through our website. Your continued use of our services constitutes acceptance of any updated terms.</p>
                </div>

              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default PrivacyPolicyPage
